import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTERS_PATH } from '../../routers/routers';
import { SearchExerciseService } from '../../services/search-exercise/search-exercise.service';
import { ExerciseQuestionsService } from '../../services/exercise-questions/exercise-questions.service';
import { useUserStore } from '../../utils/userStore';
import { useQuestionsStore } from '../../utils/questionStore';
import { useExerciseStore } from '../../utils/exerciseStore';
import { getRandomBotCompliment } from '../../constants/bot_compliments_messages';
import { hebrewLetters } from '../../configs/config';
import { showInfoToast } from '../../components/Toaster';
import { useSocraticFlow } from './helpers/useSocraticFlow';
import { useAgentChat } from './helpers/useAgentChat';
import { useSolutionCapture } from './helpers/useSolutionCapture';

// ==========================================
// Service Instances
// ==========================================
const searchExerciseService = new SearchExerciseService();
const exerciseQuestionsService = new ExerciseQuestionsService();

// ==========================================
// Types & Enums
// ==========================================
export enum PracticeStep {
  GREETING = 'GREETING',
  RECOMMEND = 'RECOMMEND',
  ENCOURAGE = 'ENCOURAGE',
  INSTRUCTIONS = 'INSTRUCTIONS',
  EXERCISE = 'EXERCISE',
  ALL_DONE = 'ALL_DONE',
  AI_CHAT = 'AI_CHAT',
  QUESTIONS_BUBBLE = 'QUESTIONS_BUBBLE',
  STEPS_BUBBLE = 'STEPS_BUBBLE'
}

export interface Message {
  id: string;
  sender: 'bot' | 'user' | 'system';
  text: string;
  type?: 'text' | 'options' | 'exercise' | 'all_done' | 'questions_list' | 'steps_list' | 'error_bubble' | 'loader_bubble' | 'chart' | 'captured_image' | 'success_banner';
  options?: {
    label: string;
    value: string;
    action?: () => void;
    icon?: React.ReactNode | string;
    iconRight?: React.ReactNode | string;
    color?: "blue" | "green" | "red" | "black" | "orange" | "green-small" | "lightBlue" | "transparent" | "purple" | "success" | "dark";
    variant?: 'contained' | 'outlined';
  }[];
  optionsClassName?: string;
  exercise?: { id: number; title: string };
  exercises?: { id: number; title: string }[];
  questions?: any[];
  steps?: any[];
  errorContext?: 'questions' | 'steps';
  selectedQuestionId?: number | null;
  activeStepIndex?: number;
  questionId?: number | null;
  questionLetter?: string | null;
  loadingText?: string;
  imageUrl?: string;
  points?: number;
  bannerMessage?: React.ReactNode;
  variant?: 'success' | 'info' | 'error';
  isGenerating?: boolean;
  qrSessionId?: string;
}

export const useExercisePractice = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo, user_attributes } = useUserStore();

  // ==========================================
  // 1. Navigation & Route Parameters
  // ==========================================
  const {
    exercise_list = [] as number[],
    topic_name = '',
    lessonId,
    levelName,
    lessonData,
    phaseIndex,
    fromDisplayExercise,
    selectedExercise: selectedExerciseFromState,
    showExerciseListDirectly
  } = location.state || {};

  const selectedExerciseIdFromStore = useExerciseStore((s) => s.selectedExerciseId);
  const exerciseId = selectedExerciseFromState?.id || selectedExerciseIdFromStore;
  const studentName = userInfo?.first_name || '';

  // ==========================================
  // 2. Hook States
  // ==========================================
  const [currentStep, setCurrentStep] = useState<PracticeStep>(() => {
    if (fromDisplayExercise) {
      return PracticeStep.QUESTIONS_BUBBLE;
    }
    if (showExerciseListDirectly) {
      return PracticeStep.EXERCISE;
    }
    return PracticeStep.GREETING;
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [exerciseTitles, setExerciseTitles] = useState<Record<number, string>>({});
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [completedMessageIds, setCompletedMessageIds] = useState<Set<string>>(new Set());
  const [exerciseHistory, setExerciseHistory] = useState<any[]>([]);
  const [loadingTitles, setLoadingTitles] = useState(false);

  const exerciseHistoryRef = useRef(exerciseHistory);
  useEffect(() => {
    exerciseHistoryRef.current = exerciseHistory;
  }, [exerciseHistory]);

  // Interactive Question & Steps Flow States (Chat Center Inline Flow)
  const [exerciseQuestions, setExerciseQuestions] = useState<any[]>([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<any | null>(null);
  const [selectedQuestionLetter, setSelectedQuestionLetter] = useState<string | null>(null);

  const [activeQuestionSteps, setActiveQuestionSteps] = useState<any[]>([]);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [stepExplanation, setStepExplanation] = useState<string>("");
  const [allStepsCompleted, setAllStepsCompleted] = useState<boolean>(false);
  const [showOptionsForStep, setShowOptionsForStep] = useState<boolean>(false);

  const selectedQuestionRef = useRef<any>(null);
  const messagesRef = useRef<Message[]>([]);
  const exerciseIdRef = useRef<number | null>(null);
  const selectedQuestionIdRef = useRef<number | null>(null);
  const selectedQuestionLetterRef = useRef<string | null>(null);
  const exerciseQuestionsRef = useRef<any[]>([]);
  const activeQuestionStepsRef = useRef<any[]>([]);
  const activeStepIndexRef = useRef<number>(0);
  const allStepsCompletedRef = useRef<boolean>(false);

  const selectQuestionFromBubbleRef = useRef<any>(null);
  const loadQuestionStepsRef = useRef<any>(null);
  const displayStepInlineRef = useRef<any>(null);
  const handleShowStepSolutionRef = useRef<any>(null);
  const handleStepTutoringRef = useRef<any>(null);
  const handleStepSolvedRef = useRef<any>(null);
  const handleExplainMoreRef = useRef<any>(null);

  useEffect(() => {
    selectedQuestionRef.current = selectedQuestion;
  }, [selectedQuestion]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    exerciseIdRef.current = exerciseId;
  }, [exerciseId]);

  useEffect(() => {
    selectedQuestionIdRef.current = selectedQuestionId;
  }, [selectedQuestionId]);

  useEffect(() => {
    selectedQuestionLetterRef.current = selectedQuestionLetter;
  }, [selectedQuestionLetter]);

  useEffect(() => {
    exerciseQuestionsRef.current = exerciseQuestions;
  }, [exerciseQuestions]);

  useEffect(() => {
    activeQuestionStepsRef.current = activeQuestionSteps;
  }, [activeQuestionSteps]);

  useEffect(() => {
    activeStepIndexRef.current = activeStepIndex;
  }, [activeStepIndex]);

  useEffect(() => {
    allStepsCompletedRef.current = allStepsCompleted;
  }, [allStepsCompleted]);

  // ==========================================
  // 3. Selectors & Memoized Computations
  // ==========================================
  const completedQuestions = useMemo(() => {
    if (!exerciseId) return [];
    const history = useQuestionsStore.getState().getQuestionHistory(exerciseId);
    return history?.completed || [];
  }, [exerciseId, messages]);

  // ==========================================
  // 4. Messaging Helpers
  // ==========================================
  const onMessageComplete = useCallback((id: string) => {
    setCompletedMessageIds(prev => new Set(prev).add(id));
  }, []);

  const completedMessageIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    completedMessageIdsRef.current = completedMessageIds;
  }, [completedMessageIds]);

  const waitForMessageCompletion = useCallback((id: string) => {
    return new Promise<void>((resolve) => {
      const check = () => {
        if (completedMessageIdsRef.current.has(id)) {
          resolve();
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }, []);

  const addBotMessage = useCallback((msg: Partial<Message>, delay = 800) => {
    const shouldShowTyping = !(msg.type === 'options' && !msg.text);
    if (shouldShowTyping) {
      setIsTyping(true);
    }
    const id = msg.id || (Date.now().toString() + Math.random());
    return new Promise<string>((resolve) => {
      setTimeout(() => {
        setMessages(prev => {
          if (prev.some(m => m.id === id)) return prev;
          return [...prev, {
            id,
            sender: 'bot',
            text: '',
            type: 'text',
            ...msg
          }];
        });
        if (shouldShowTyping) {
          setIsTyping(false);
        }
        resolve(id);
      }, delay);
    });
  }, []);

  // ==========================================
  // 5. Custom Sub-Hook Invocations
  // ==========================================
  const handleCaptureStartRef = useRef<() => void>();
  const handleCaptureStopRef = useRef<() => void>();

  const isFromLessonList = !!(location.state?.lessonId && location.state?.returnToExerciseList);

  const socraticFlow = useSocraticFlow({
    isFromLessonList,
    exerciseId,
    exerciseIdRef,
    selectedQuestionId,
    selectedQuestionIdRef,
    setSelectedQuestionId,
    selectedQuestion,
    setSelectedQuestion,
    selectedQuestionRef,
    selectedQuestionLetter,
    selectedQuestionLetterRef,
    setSelectedQuestionLetter,
    activeQuestionSteps,
    activeQuestionStepsRef,
    setActiveQuestionSteps,
    activeStepIndex,
    activeStepIndexRef,
    setActiveStepIndex,
    allStepsCompleted,
    allStepsCompletedRef,
    setAllStepsCompleted,
    showOptionsForStep,
    setShowOptionsForStep,
    exerciseQuestions,
    exerciseQuestionsRef,
    setExerciseQuestions,
    isTyping,
    setIsTyping,
    messages,
    messagesRef,
    setMessages,
    addBotMessage,
    waitForMessageCompletion,
    studentName,
    user_attributes,
    t,
    handleCaptureStart: () => handleCaptureStartRef.current?.(),
    handleCaptureStop: () => handleCaptureStopRef.current?.()
  });

  const {
    selectQuestionFromBubble,
    loadQuestionSteps,
    displayStepInline,
    handleStepSolved,
    handleExplainMore,
    handleShowStepSolution,
    handleStepTutoring
  } = socraticFlow;

  selectQuestionFromBubbleRef.current = selectQuestionFromBubble;
  loadQuestionStepsRef.current = loadQuestionSteps;
  displayStepInlineRef.current = displayStepInline;
  handleShowStepSolutionRef.current = handleShowStepSolution;
  handleStepTutoringRef.current = handleStepTutoring;
  handleStepSolvedRef.current = handleStepSolved;
  handleExplainMoreRef.current = handleExplainMore;

  const solutionCapture = useSolutionCapture({
    exerciseId,
    selectedQuestionId,
    selectedQuestionLetter,
    activeQuestionSteps,
    activeStepIndex,
    addBotMessage,
    setMessages,
    t,
    user_attributes,
    handleShowStepSolution,
    handleStepSolved,
    studentName,
    setExerciseQuestions,
    exerciseTitle: selectedExerciseFromState?.title || (exerciseId ? exerciseTitles[exerciseId] : ''),
    topicName: topic_name || selectedExerciseFromState?.topic_name || '',
    difficultyName: selectedExerciseFromState?.difficulty_name || '',
    levelUnits: selectedExerciseFromState?.level_units ?? (levelName ? parseInt(levelName) : null),
    questionText: selectedQuestion?.question_text || ''
  });
  handleCaptureStartRef.current = solutionCapture.startCapture;
  handleCaptureStopRef.current = solutionCapture.stopCapture;

  const agentChat = useAgentChat({
    isFromLessonList,
    topic_name,
    lessonId,
    levelName,
    lessonData,
    phaseIndex,
    fromDisplayExercise,
    currentStep,
    setCurrentStep,
    setIsTyping,
    messages,
    messagesRef,
    setMessages,
    addBotMessage,
    waitForMessageCompletion,
    studentName,
    user_attributes,
    t,

    selectedQuestionId,
    selectedQuestionIdRef,
    setSelectedQuestionId,
    selectedQuestion,
    setSelectedQuestion,
    selectedQuestionRef,
    selectedQuestionLetter,
    selectedQuestionLetterRef,
    setSelectedQuestionLetter,
    activeQuestionSteps,
    activeQuestionStepsRef,
    activeStepIndex,
    activeStepIndexRef,
    allStepsCompleted,
    allStepsCompletedRef,
    exerciseQuestions,
    exerciseQuestionsRef,
    exerciseId,

    selectQuestionFromBubble: (...args) => selectQuestionFromBubbleRef.current?.(...args),
    loadQuestionSteps: (...args) => loadQuestionStepsRef.current?.(...args),
    handleShowStepSolution: (...args) => handleShowStepSolutionRef.current?.(...args),
    handleStepTutoring: (...args) => handleStepTutoringRef.current?.(...args),
    handleStepSolved: (...args) => handleStepSolvedRef.current?.(...args),
    handleExplainMore: (...args) => handleExplainMoreRef.current?.(...args),
    handleCaptureStart: () => handleCaptureStartRef.current?.()
  });

  const {
    sendMessageToAgent,
    handleResumeFromOffTopic,
    addUserMessage,
    handleProgressChoice
  } = agentChat;

  // ==========================================
  // 6. Lesson Progression Helper Callbacks
  // ==========================================
  const showTransitionIntro = useCallback(async () => {
    setIsTyping(true);
    await new Promise((resolve) => setTimeout(resolve, 20));

    const introMsgId = 'loading-intro-msg';
    setMessages(prev => {
      if (prev.some(m => m.id === introMsgId)) return prev;
      return [
        ...prev,
        {
          id: introMsgId,
          sender: 'bot',
          text: t('exercisePracticeLoadingQuestions'),
          type: 'text'
        }
      ];
    });
    setIsTyping(false);
  }, [t]);

  const loadQuestionsList = useCallback(async (retry: boolean = false) => {
    if (!exerciseId) return;

    if (retry) {
      setMessages(prev => prev.filter(m => m.type !== 'error_bubble'));
    }

    setIsTyping(true);

    try {
      const questions = await exerciseQuestionsService.fetchExerciseQuestions(exerciseId);
      if (!questions || questions.length === 0) {
        throw new Error("No questions returned");
      }

      let completed: number[] = [];
      let in_process: number[] = [];

      const historyResponse = await exerciseQuestionsService.getExerciseQuestionHistory(exerciseId);
      if (historyResponse?.questions?.length > 0) {
        const normalized = historyResponse.questions[0];
        useQuestionsStore.getState().setQuestionHistory({
          exercise_id: exerciseId,
          current: normalized.current || [],
          completed: normalized.completed || [],
          in_process: normalized.in_process || [],
        });
        completed = normalized.completed || [];
        in_process = normalized.in_process || [];
      }

      questions.forEach((q: any) => {
        if (q.chart_image_data) {
          useQuestionsStore.getState().setChart({
            question_id: q.id,
            chart_image_base64: q.chart_image_data,
          });
        }
      });

      const mappedQuestions = questions.map((q: any) => ({
        ...q,
        isCompleted: completed.includes(q.id),
        isInProcess: in_process.includes(q.id)
      }));

      setExerciseQuestions(mappedQuestions);

      const listBubbleId = 'questions-list-bubble';
      setMessages(prev => {
        if (prev.some(m => m.id === listBubbleId)) return prev;
        return [
          ...prev,
          {
            id: listBubbleId,
            sender: 'bot',
            text: t('sectionsOfTheExercise'),
            type: 'questions_list',
            questions: mappedQuestions
          }
        ];
      });

    } catch (error) {
      console.error("Failed to load exercise questions:", error);
      setMessages(prev => [
        ...prev,
        {
          id: 'questions-error',
          sender: 'bot',
          text: t("exercisePracticeConnectionError"),
          type: 'error_bubble',
          errorContext: 'questions'
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  }, [exerciseId, setExerciseQuestions, setSelectedQuestionId, setSelectedQuestion, setSelectedQuestionLetter, setMessages, setIsTyping, t]);

  const handleContinueLesson = useCallback(() => {
    if (location.state?.returnToExerciseList) {
      navigate(ROUTERS_PATH.EXERCISE_PRACTICE, {
        state: {
          ...location.state,
          showExerciseListDirectly: true,
          fromDisplayExercise: false,
          returnToExerciseList: false,
          selectedExercise: undefined
        }
      });
    } else {
      navigate(ROUTERS_PATH.PLAY_LESSON, {
        state: {
          lessonId,
          resume: true,
          levelName,
          topicName: topic_name,
          lessonData
        }
      });
    }
  }, [navigate, lessonId, levelName, topic_name, lessonData, location.state]);

  const handleGoToNextStep = useCallback(() => {
    const isFinal = location.state?.isFinalSummary;
    if (isFinal) {
      navigate(ROUTERS_PATH.SEARCH_LESSON, { state: { lessonId } });
    } else {
      navigate(ROUTERS_PATH.PLAY_LESSON, {
        state: {
          lessonId,
          resume: true,
          levelName,
          topicName: topic_name,
          lessonData
        }
      });
    }
  }, [navigate, lessonId, levelName, topic_name, lessonData, location.state]);

  const handleContinueLater = useCallback(() => {
    navigate(ROUTERS_PATH.PLAY_LESSON, {
      state: {
        lessonId,
        resume: true,
        levelName,
        topicName: topic_name,
        lessonData
      }
    });
  }, [navigate, lessonId, levelName, topic_name, lessonData]);

  // ==========================================
  // 7. Lifecycle Side-Effects
  // ==========================================
  useEffect(() => {
    const fetchTitles = async () => {
      if (exercise_list.length > 0) {
        setLoadingTitles(true);
        try {
          const titles = await searchExerciseService.getExerciseTitles(exercise_list);
          const titleMap = titles.reduce((acc: any, curr: any) => {
            acc[curr.id] = curr.title;
            return acc;
          }, {});
          setExerciseTitles(titleMap);
        } catch (err) {
          console.error("Failed to load exercise titles", err);
        } finally {
          setLoadingTitles(false);
        }
      }
    };
    fetchTitles();
  }, [exercise_list]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await exerciseQuestionsService.getExerciseHistory();
        if (res?.exercises?.length) {
          setExerciseHistory(res.exercises);
        }
      } catch (err) {
        console.error("Failed to load exercise history", err);
      }
    };
    fetchHistory();
  }, []);

  // Reactively reset state and refetch history/titles when showExerciseListDirectly becomes true
  useEffect(() => {
    if (showExerciseListDirectly) {
      setMessages([]);
      setCompletedMessageIds(new Set());
      setCurrentStep(PracticeStep.EXERCISE);
      setSelectedQuestionId(null);
      setSelectedQuestion(null);
      setSelectedQuestionLetter(null);
      setActiveQuestionSteps([]);
      setActiveStepIndex(0);
      setAllStepsCompleted(false);
      setShowOptionsForStep(false);

      const refetchData = async () => {
        try {
          const res = await exerciseQuestionsService.getExerciseHistory();
          if (res?.exercises?.length) {
            setExerciseHistory(res.exercises);
          }
        } catch (err) {
          console.error("Failed to reload exercise history:", err);
        }

        if (exercise_list.length > 0) {
          setLoadingTitles(true);
          try {
            const titles = await searchExerciseService.getExerciseTitles(exercise_list);
            const titleMap = titles.reduce((acc: any, curr: any) => {
              acc[curr.id] = curr.title;
              return acc;
            }, {});
            setExerciseTitles(titleMap);
          } catch (err) {
            console.error("Failed to reload exercise titles:", err);
          } finally {
            setLoadingTitles(false);
          }
        }
      };
      refetchData();
    }
  }, [showExerciseListDirectly, exercise_list]);

  useEffect(() => {
    if (messages.length === 0) {
      if (fromDisplayExercise) {
        showTransitionIntro();
      } else if (showExerciseListDirectly) {
        // Do nothing, the exercise list will be displayed by the currentStep effect
      } else {
        const genderNum = user_attributes?.gender;
        const genderStr = genderNum === 2 ? 'female' : 'male';
        const greetingKey = genderStr === 'female' ? 'exercisePracticeGreeting_female' : 'exercisePracticeGreeting_male';

        addBotMessage({
          id: 'greeting',
          text: t(greetingKey, { studentName: studentName || '' }),
          type: 'options',
          options: [
            {
              label: t('exercisePracticeFeelsProgress'),
              value: 'progress',
              action: () => handleProgressChoice('progress')
            },
            {
              label: t('exercisePracticeNotThereYet'),
              value: 'not_yet',
              action: () => handleProgressChoice('not_yet')
            }
          ]
        });
      }
    }
  }, [addBotMessage, messages.length, studentName, t, user_attributes?.gender, fromDisplayExercise, showTransitionIntro, handleProgressChoice, showExerciseListDirectly]);

  useEffect(() => {
    if (fromDisplayExercise && completedMessageIds.has('loading-intro-msg')) {
      const hasQuestionsList = messages.some(m => m.id === 'questions-list-bubble');
      const hasError = messages.some(m => m.id === 'questions-error');

      if (!hasQuestionsList && !hasError && !isTyping) {
        loadQuestionsList();
      }
    }
  }, [fromDisplayExercise, completedMessageIds, messages, isTyping, loadQuestionsList]);

  useEffect(() => {
    if (!fromDisplayExercise || !exerciseQuestions || exerciseQuestions.length === 0) return;
    const listMsgId = 'questions-list-bubble';
    const promptMsgId = 'select-section-prompt';

    if (completedMessageIds.has(listMsgId) && !messages.some(m => m.id === promptMsgId)) {
      const showPrompt = async () => {
        const genderNum = user_attributes?.gender;
        const genderStr = genderNum === 2 ? 'female' : 'male';
        const promptText = t(`exercisePracticeSelectSectionPrompt_${genderStr}`, { studentName });

        await addBotMessage({
          id: promptMsgId,
          text: promptText,
          type: 'text',
          options: [
            {
              label: t("wayToSolution"),
              value: "way_to_solution",
              action: () => {
                const targetQ = exerciseQuestions[0];
                if (targetQ) {
                  selectQuestionFromBubble(targetQ, hebrewLetters[0]);
                }
              }
            }
          ]
        }, 20);
      };
      showPrompt();
    }
  }, [fromDisplayExercise, completedMessageIds, messages, exerciseQuestions, user_attributes, studentName, selectQuestionFromBubble, t, addBotMessage]);

  useEffect(() => {
    if (!selectedQuestionId) return;
    const qId = selectedQuestionId;
    const listMsgId = `steps-list-bubble-${qId}`;
    const promptMsgId = `steps-select-prompt-${qId}`;

    if (completedMessageIds.has(listMsgId) && !messages.some(m => m.id === promptMsgId)) {
      setIsTyping(true);
      const timer = setTimeout(() => {
        setIsTyping(false);
        const genderNum = user_attributes?.gender;
        const genderStr = genderNum === 2 ? 'female' : 'male';
        const promptText = t(`exercisePracticeExplainStepsSelectPrompt_${genderStr}`, { studentName });
        
        setMessages(prev => {
          if (prev.some(m => m.id === promptMsgId)) return prev;
          return [
            ...prev,
            {
              id: promptMsgId,
              sender: 'bot',
              text: promptText,
              type: 'text',
              options: [
                {
                  label: t("exercisePracticeUnderstoodToSolve"),
                  value: "understood_to_solve",
                  action: () => {
                    setMessages(prevMsgs => prevMsgs.map(m => m.id === promptMsgId ? { ...m, options: [] } : m));
                    displayStepInline(0, activeQuestionSteps, qId, selectedQuestionLetter || hebrewLetters[0]);
                  }
                }
              ]
            }
          ];
        });
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [selectedQuestionId, completedMessageIds, messages, user_attributes, studentName, activeQuestionSteps, selectedQuestionLetter, displayStepInline, t, setIsTyping, setMessages]);

  useEffect(() => {
    if (fromDisplayExercise) return;

    if (currentStep === PracticeStep.RECOMMEND) {
      const msg1Id = 'recommend-1';
      const msg2Id = 'recommend-2';

      if (!messages.some(m => m.id === msg1Id)) {
        addBotMessage({
          id: msg1Id,
          text: t('exercisePracticeHappyToHear', { studentName })
        });
      } else if (completedMessageIds.has(msg1Id) && !messages.some(m => m.id === msg2Id)) {
        const genderNum = user_attributes?.gender;
        const genderStr = genderNum === 2 ? 'female' : 'male';
        const recommendKey = genderStr === 'female' ? 'exercisePracticeRecommend_female' : 'exercisePracticeRecommend_male';

        addBotMessage({
          id: msg2Id,
          text: t(recommendKey, { topic: topic_name, studentName })
        });
      } else if (completedMessageIds.has(msg2Id)) {
        setCurrentStep(PracticeStep.INSTRUCTIONS);
      }
    } else if (currentStep === PracticeStep.ENCOURAGE) {
      const msgId = 'encourage';
      if (!messages.some(m => m.id === msgId)) {
        addBotMessage({
          id: msgId,
          text: t('exercisePracticeEncourage'),
          type: 'options',
          options: [
            {
              label: t('exercisePracticeFeelsProgress'),
              value: 'progress',
              action: () => setCurrentStep(PracticeStep.RECOMMEND)
            }
          ]
        });
      }
    } else if (currentStep === PracticeStep.INSTRUCTIONS) {
      const msgId = 'instructions';
      if (!messages.some(m => m.id === msgId)) {
        const genderNum = user_attributes?.gender;
        const genderStr = genderNum === 2 ? 'female' : 'male';
        const instructionsKey = genderStr === 'female' ? 'exercisePracticeInstructions_female' : 'exercisePracticeInstructions_male';

        addBotMessage({
          id: msgId,
          text: t(instructionsKey),
          type: 'options',
          options: [
            { label: t('exercisePracticeLetsStart'), value: 'start', action: () => setCurrentStep(PracticeStep.EXERCISE) },
            { label: t('exercisePracticeContinueLater'), value: 'later', action: handleContinueLater }
          ]
        });
      }
    } else if (currentStep === PracticeStep.EXERCISE) {
      const msgId = 'exercises-list';

      if (!messages.some(m => m.id === msgId)) {
        const exercisesList = exercise_list.map((exId, idx) => {
          const historyItem = exerciseHistory.find((h: any) => h.exercise_id === exId);
          return {
            id: exId,
            title: exerciseTitles[exId] || t('exercisePracticeExerciseLabelNumbered', { number: idx + 1 }),
            isCompleted: historyItem?.is_completed === true,
            isInProcess: historyItem?.is_completed === false
          };
        });

        const initialHasIncomplete = exercise_list.some(exId => {
          const historyItem = exerciseHistory.find((h: any) => h.exercise_id === exId);
          return historyItem?.is_completed !== true;
        });

        if (initialHasIncomplete) {
          addBotMessage({
            id: msgId,
            text: t('exercisePracticeClickExercise'),
            type: 'exercise',
            exercises: exercisesList,
            options: [
              {
                label: t('continueLesson'),
                value: 'continue',
                action: () => {
                  const hasIncomplete = exercise_list.some(exId => {
                    const historyItem = exerciseHistoryRef.current.find((h: any) => h.exercise_id === exId);
                    return historyItem?.is_completed !== true;
                  });
                  
                  const genderNum = user_attributes?.gender;
                  const genderStr = genderNum === 2 ? 'female' : 'male';
                  
                  const isFinal = location.state?.isFinalSummary;
                  let msgText = '';
                  if (hasIncomplete) {
                    msgText = isFinal
                      ? t(`exercisePracticeIncompleteListFinalPrompt_${genderStr}`, { studentName })
                      : t(`exercisePracticeIncompleteListPrompt_${genderStr}`, { 
                          studentName, 
                          topicName: topic_name 
                        });
                  } else {
                    msgText = isFinal
                      ? t(`exercisePracticeAllListCompletedPromptFinal_${genderStr}`, { 
                          studentName,
                          topicName: topic_name
                        })
                      : t(`exercisePracticeAllListCompletedPromptNew_${genderStr}`, { 
                          studentName,
                          topicName: topic_name
                        });
                  }

                  addBotMessage({
                    id: `list-continue-prompt-${Date.now()}`,
                    text: msgText,
                    type: 'options',
                    options: [
                      {
                        label: hasIncomplete ? t('continueLesson') : (isFinal ? t('moreLessons') : t('nextLesson')),
                        value: 'continue',
                        action: hasIncomplete 
                          ? (isFinal ? handleGoToNextStep : handleContinueLesson)
                          : handleGoToNextStep
                      }
                    ]
                  });
                }
              }
            ]
          });
        } else {
          addBotMessage({
            id: msgId,
            text: t('exercisePracticeClickExercise'),
            type: 'exercise',
            exercises: exercisesList
          });

          const genderNum = user_attributes?.gender;
          const genderStr = genderNum === 2 ? 'female' : 'male';
          const isFinal = location.state?.isFinalSummary;
          const successMsgText = isFinal
            ? t(`exercisePracticeAllListCompletedPromptFinal_${genderStr}`, { 
                studentName,
                topicName: topic_name
              })
            : t(`exercisePracticeAllListCompletedPromptNew_${genderStr}`, { 
                studentName,
                topicName: topic_name
              });

          addBotMessage({
            id: `list-success-prompt-${Date.now()}`,
            text: successMsgText,
            type: 'options',
            options: [
              {
                label: isFinal ? t('moreLessons') : t('nextLesson'),
                value: 'continue',
                action: handleGoToNextStep
              }
            ]
          });
        }
      }
    } else if (currentStep === PracticeStep.ALL_DONE) {
      const msgId = 'all-done';
      const isFinal = location.state?.isFinalSummary;
      if (!messages.some(m => m.id === msgId)) {
        addBotMessage({
          id: msgId,
          text: t('exercisePracticeAllDone', { studentName }),
          type: 'all_done',
          options: [
            { label: isFinal ? t('moreLessons') : t('nextLesson'), value: 'continue', action: handleGoToNextStep }
          ]
        });
      }
    }
  }, [
    currentStep,
    addBotMessage,
    studentName,
    t,
    topic_name,
    exercise_list,
    currentExerciseIndex,
    exerciseTitles,
    exerciseHistory,
    completedMessageIds,
    messages,
    fromDisplayExercise,
    handleContinueLater,
    handleContinueLesson,
    handleGoToNextStep,
    setCurrentStep,
    setCurrentExerciseIndex
  ]);

  return {
    messages,
    isTyping,
    addUserMessage,
    onMessageComplete,
    completedMessageIds,
    handleContinueLesson,
    handleContinueLater,
    handleGoToNextStep,
    exerciseTitles,
    exerciseHistory,
    loadingTitles,
    exercise_list,

    // Inline Question and Steps Flow variables/actions
    fromDisplayExercise,
    exerciseQuestions,
    selectedQuestionId,
    selectedQuestion,
    selectedQuestionLetter,
    loadQuestionsList,
    selectQuestionFromBubble,
    loadQuestionSteps,
    displayStepInline,
    handleResumeFromOffTopic,
    startCapture: solutionCapture.startCapture,
    isUploading: solutionCapture.isUploading,
    capturedPages: solutionCapture.capturedPages,
    removePage: solutionCapture.removePage,
    isMobileActive: solutionCapture.isMobileActive,
    qrSessionId: solutionCapture.qrSessionId,
    startManualCapture: solutionCapture.startManualCapture,
    isPolling: solutionCapture.isPolling,
    fileToCrop: solutionCapture.fileToCrop,
    setFileToCrop: solutionCapture.setFileToCrop,
    handleCroppedFile: solutionCapture.handleCroppedFile,
    solutionAnalysisData: solutionCapture.solutionAnalysisData,
    setSolutionAnalysisData: solutionCapture.setSolutionAnalysisData,
    isAnalysisModalOpen: solutionCapture.isAnalysisModalOpen,
    setIsAnalysisModalOpen: solutionCapture.setIsAnalysisModalOpen
  };
};
